import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, X, Search } from 'lucide-react';
import { useFavicon } from '../hooks/useFavicon';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const HeaderNav = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({ logoUrl: '', faviconUrl: '', siteName: 'B&B MAG' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Apply favicon from settings
  useFavicon(settings.faviconUrl);

  // Live search with debounce
  useEffect(() => {
    const delaySearch = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        try {
          const response = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&pageSize=5`);
          setSearchResults(response.data);
          setShowDropdown(true);
        } catch (error) {
          console.error('Search error:', error);
        }
      } else {
        setSearchResults(null);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowDropdown(false);
      setSearchQuery('');
    }
  };

  const navItems = [
    { label: 'Features', href: '/features' },
    { label: 'Interviews', href: '/interviews' },
    { label: 'Brands', href: '/brands' },
    { label: 'Creators', href: '/creators' },
    { label: 'Videos', href: '/videos' },
    { label: 'Shorts', href: '/shorts' },
    { label: 'Covers', href: '/covers' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="site-logo-link"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-200"
          >
            {settings.logoUrl ? (
              <img 
                src={settings.logoUrl} 
                alt={settings.siteName} 
                className="h-8 object-contain"
                style={{ maxHeight: '32px' }}
              />
            ) : (
              <div className="font-display text-2xl font-bold tracking-tight">
                <span className="text-primary">B&B</span>
                <span className="text-muted-foreground text-base"> MAG</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <nav className="flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-sm font-ui font-medium text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Search */}
            <div className="relative" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-[200px] pl-9 pr-3 h-9"
                />
              </form>

              {/* Live Search Dropdown */}
              {showDropdown && searchResults && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full mt-2 w-[400px] max-h-[500px] overflow-y-auto bg-[hsl(var(--surface-1))] border border-border rounded-lg shadow-xl z-50"
                >
                  {searchResults.total === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No results found
                    </div>
                  ) : (
                    <div className="p-2">
                      {/* Articles */}
                      {searchResults.articles.length > 0 && (
                        <div className="mb-3">
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                            Articles ({searchResults.articles.length})
                          </div>
                          {searchResults.articles.slice(0, 3).map((article) => (
                            <Link
                              key={article.id}
                              to={`/articles/${article.slug}`}
                              onClick={() => {
                                setShowDropdown(false);
                                setSearchQuery('');
                              }}
                              className="block px-3 py-2 hover:bg-[hsl(var(--surface-2))] rounded transition-colors"
                            >
                              <div className="font-medium text-sm">{article.title}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{article.dek}</div>
                            </Link>
                          ))}
                        </div>
                      )}

                      {/* Videos */}
                      {searchResults.videos.length > 0 && (
                        <div className="mb-3">
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                            Videos ({searchResults.videos.length})
                          </div>
                          {searchResults.videos.slice(0, 2).map((video) => (
                            <Link
                              key={video.id}
                              to={`/videos/${video.slug}`}
                              onClick={() => {
                                setShowDropdown(false);
                                setSearchQuery('');
                              }}
                              className="block px-3 py-2 hover:bg-[hsl(var(--surface-2))] rounded transition-colors"
                            >
                              <div className="font-medium text-sm">{video.title}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{video.dek}</div>
                            </Link>
                          ))}
                        </div>
                      )}

                      {/* View All Link */}
                      <div className="border-t border-border mt-2 pt-2">
                        <Link
                          to={`/search?q=${encodeURIComponent(searchQuery)}`}
                          onClick={() => {
                            setShowDropdown(false);
                            setSearchQuery('');
                          }}
                          className="block px-3 py-2 text-sm text-primary hover:bg-[hsl(var(--surface-2))] rounded transition-colors text-center font-medium"
                        >
                          View all {searchResults.total} results →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button 
                variant="ghost" 
                size="sm" 
                data-testid="mobile-menu-button"
                className="h-9 w-9 p-0"
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px]">
              {/* Mobile Search */}
              <div className="mt-6 mb-6">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                    setIsOpen(false);
                    setSearchQuery('');
                  }
                }} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </form>
              </div>

              <nav className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className="font-ui text-lg font-medium text-foreground hover:text-primary transition-colors duration-200 py-2 active:text-primary"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="mt-4 pt-4 border-t border-border">
                  <Link
                    to="/admin/login"
                    onClick={() => setIsOpen(false)}
                    className="font-ui text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    Admin Login
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};